/**
 * Bolas de neve: com neve no chão, apanha-se uma (F), e atira-se (F de novo
 * para quem está à frente, ou um clique em alguém / num sítio do jardim).
 *
 * O lançamento vai ao servidor, que o manda ao escritório todo
 * (`SpaceBolaDeNeve`): toda a gente vê a bola voar e rebentar. Aqui fica o
 * que se vê — a bola na mão, o voo em arco, o rebentar, a neve agarrada a quem
 * levou com ela — e as contas, puras e testadas.
 */
import { BOLA_DE_NEVE_ALCANCE_PX, type Facing, TILE } from "@greenvoice/shared";
import type PhaserT from "phaser";

// ── As contas ──────────────────────────────────────────────────────────────

export interface BolaNoAr {
  id: string;
  userId: string;
  deX: number;
  deY: number;
  paraX: number;
  paraY: number;
  targetId: string | null;
}

/** Quanto demora o voo: mais longe, mais tempo, dentro de limites. */
export function duracaoDoVoo(distancia: number): number {
  return Math.max(260, Math.min(900, distancia * 2.4));
}

/** Altura do arco a meio do voo. */
export function alturaDoArco(distancia: number): number {
  return Math.min(56, 12 + distancia * 0.2);
}

/** Onde está a bola a uma fracção `k` do voo: o ponto no chão e a altura por cima dele. */
export function pontoDoVoo(
  de: { x: number; y: number },
  para: { x: number; y: number },
  k: number,
): { x: number; y: number; altura: number } {
  const t = Math.min(1, Math.max(0, k));
  const d = Math.hypot(para.x - de.x, para.y - de.y);
  return {
    x: de.x + (para.x - de.x) * t,
    y: de.y + (para.y - de.y) * t,
    altura: Math.sin(t * Math.PI) * alturaDoArco(d),
  };
}

const DIRECOES: Record<Facing, { x: number; y: number }> = {
  up: { x: 0, y: -1 },
  down: { x: 0, y: 1 },
  left: { x: -1, y: 0 },
  right: { x: 1, y: 0 },
};

/** O ponto a `distancia` px à frente de quem olha para `dir`. */
export function pontoAFrente(eu: { x: number; y: number }, dir: Facing, distancia: number): { x: number; y: number } {
  const d = DIRECOES[dir];
  return { x: eu.x + d.x * distancia, y: eu.y + d.y * distancia };
}

/**
 * A pessoa mais perto à frente (num cone de ~70°) e ao alcance — é para ela
 * que vai a bola quando se carrega em F sem apontar.
 */
export function alvoAFrente<T extends { x: number; y: number }>(
  eu: { x: number; y: number },
  dir: Facing,
  pessoas: readonly T[],
  alcance = BOLA_DE_NEVE_ALCANCE_PX,
): T | null {
  const d = DIRECOES[dir];
  let melhor: T | null = null;
  let melhorDist = Number.POSITIVE_INFINITY;
  for (const p of pessoas) {
    const dx = p.x - eu.x;
    const dy = p.y - eu.y;
    const dist = Math.hypot(dx, dy);
    if (dist < 4 || dist > alcance) continue;
    const cos = (dx * d.x + dy * d.y) / dist;
    if (cos < Math.cos((35 * Math.PI) / 180)) continue;
    if (dist < melhorDist) {
      melhor = p;
      melhorDist = dist;
    }
  }
  return melhor;
}

export type EstadoDaBola = "sem_neve" | "pode" | "a_apanhar" | "na_mao";

/** Quanto demora a apanhar neve do chão e fazer a bola. */
export const APANHAR_MS = 450;

/** A que altura dos pés a bola acerta no corpo. */
const ALTURA_DO_CORPO = 22;

// ── A arte ─────────────────────────────────────────────────────────────────

const T_BOLA = "bola:bola";
const T_MARCA = "bola:marca";
const T_PEDACO = "bola:pedaco";

function texturas(scene: PhaserT.Scene): void {
  const tela = (chave: string, w: number, h: number, pintar: (c: CanvasRenderingContext2D) => void) => {
    if (scene.textures.exists(chave)) return;
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    pintar(ctx);
    scene.textures.addCanvas(chave, canvas);
  };
  tela(T_BOLA, 10, 10, (c) => {
    c.fillStyle = "#9fb6cc";
    c.beginPath();
    c.arc(5, 5, 4.5, 0, Math.PI * 2);
    c.fill();
    c.fillStyle = "#f7fbff";
    c.beginPath();
    c.arc(4.4, 4.4, 3.7, 0, Math.PI * 2);
    c.fill();
    c.fillStyle = "#ffffff";
    c.fillRect(3, 2, 2, 2);
  });
  tela(T_MARCA, 22, 14, (c) => {
    c.fillStyle = "rgba(247, 251, 255, 0.95)";
    for (const [x, y, r] of [
      [11, 7, 5],
      [6, 6, 3],
      [16, 8, 3.5],
      [9, 11, 2.5],
      [14, 3, 2.5],
    ] as const) {
      c.beginPath();
      c.arc(x, y, r, 0, Math.PI * 2);
      c.fill();
    }
  });
  tela(T_PEDACO, 3, 3, (c) => {
    c.fillStyle = "#f7fbff";
    c.fillRect(0, 0, 3, 3);
  });
}

// ── A peça no mundo ────────────────────────────────────────────────────────

interface Voo {
  bola: BolaNoAr;
  inicio: number;
  duracao: number;
  img: PhaserT.GameObjects.Image;
  sombra: PhaserT.GameObjects.Ellipse;
}

interface Marca {
  userId: string;
  img: PhaserT.GameObjects.Image;
  ate: number;
  texto: PhaserT.GameObjects.Text;
}

/** O que a cena precisa de dizer, a cada frame, para as bolas se desenharem. */
export interface QuadroDasBolas {
  eu: { x: number; y: number; dir: Facing };
  /** Há neve para apanhar debaixo dos pés? */
  podeApanhar: boolean;
  /** Onde estão os pés de uma pessoa (o próprio incluído), ou `null`. */
  posicao: (userId: string) => { x: number; y: number } | null;
  /** As bolas que o servidor mandou (as mais recentes no fim). */
  bolas: readonly BolaNoAr[];
}

const porCena = new WeakMap<PhaserT.Scene, BolasDeNeve>();

export function bolasDaCena(scene: PhaserT.Scene): BolasDeNeve | undefined {
  return porCena.get(scene);
}

export class BolasDeNeve {
  private estadoAtual: EstadoDaBola = "sem_neve";
  private apanharDesde = 0;
  private readonly naMao: PhaserT.GameObjects.Image;
  private readonly vistas = new Set<string>();
  private readonly voos: Voo[] = [];
  private readonly marcas: Marca[] = [];
  private readonly chao: { img: PhaserT.GameObjects.Image; ate: number }[] = [];
  private readonly pedacos: PhaserT.GameObjects.Particles.ParticleEmitter;
  private primeiroFrame = true;

  constructor(
    private readonly scene: PhaserT.Scene,
    private readonly aoMudar: (estado: EstadoDaBola) => void,
  ) {
    texturas(scene);
    this.naMao = scene.add.image(0, 0, T_BOLA).setVisible(false);
    this.pedacos = scene.add
      .particles(0, 0, T_PEDACO, {
        lifespan: { min: 300, max: 600 },
        speed: { min: 40, max: 130 },
        angle: { min: 0, max: 360 },
        gravityY: 260,
        scale: { min: 0.6, max: 1.4 },
        alpha: { start: 1, end: 0 },
        emitting: false,
      })
      .setDepth(1_000_001);
    porCena.set(scene, this);
  }

  get estado(): EstadoDaBola {
    return this.estadoAtual;
  }

  /** F sem bola: começa a apanhar neve. Devolve se começou. */
  apanhar(agora: number, eu: { x: number; y: number }): boolean {
    if (this.estadoAtual !== "pode") return false;
    this.apanharDesde = agora;
    this.pedacos.explode(8, eu.x, eu.y - 2);
    this.mudar("a_apanhar");
    return true;
  }

  /** Larga a bola que está na mão (vai ser atirada). Devolve se havia bola. */
  largar(): boolean {
    if (this.estadoAtual !== "na_mao") return false;
    this.mudar("pode");
    return true;
  }

  update(agora: number, q: QuadroDasBolas): void {
    // O estado da mão.
    if (this.estadoAtual === "a_apanhar" && agora - this.apanharDesde >= APANHAR_MS) this.mudar("na_mao");
    if (this.estadoAtual === "sem_neve" && q.podeApanhar) this.mudar("pode");
    if (this.estadoAtual === "pode" && !q.podeApanhar) this.mudar("sem_neve");
    // Uma bola na mão aguenta entrar no prédio; só derrete quando a neve do
    // chão acaba de vez (a cena chama `semNeve`).

    const mao = this.estadoAtual === "na_mao";
    this.naMao.setVisible(mao);
    if (mao) {
      const lado = q.eu.dir === "left" ? -1 : q.eu.dir === "right" ? 1 : q.eu.dir === "down" ? 0.8 : -0.8;
      this.naMao
        .setPosition(q.eu.x + lado * 10, q.eu.y - 16 + Math.sin(agora / 180) * 0.8)
        .setDepth(q.eu.y + (q.eu.dir === "up" ? -1 : 1));
    }

    // As bolas novas que o servidor mandou. No primeiro frame as que já lá
    // estavam contam como vistas: não se repetem voos antigos ao entrar.
    for (const bola of q.bolas) {
      if (this.vistas.has(bola.id)) continue;
      this.vistas.add(bola.id);
      if (this.primeiroFrame) continue;
      const d = Math.hypot(bola.paraX - bola.deX, bola.paraY - bola.deY);
      this.voos.push({
        bola,
        inicio: agora,
        duracao: duracaoDoVoo(d),
        img: this.scene.add.image(bola.deX, bola.deY - ALTURA_DO_CORPO, T_BOLA),
        sombra: this.scene.add.ellipse(bola.deX, bola.deY, 8, 3, 0x000000, 0.28),
      });
    }
    this.primeiroFrame = false;
    if (this.vistas.size > 200) this.vistas.clear();

    // Os voos.
    for (let i = this.voos.length - 1; i >= 0; i -= 1) {
      const v = this.voos[i];
      if (!v) continue;
      const k = (agora - v.inicio) / v.duracao;
      const alvo = v.bola.targetId ? q.posicao(v.bola.targetId) : null;
      const destino = alvo ?? { x: v.bola.paraX, y: v.bola.paraY };
      const p = pontoDoVoo({ x: v.bola.deX, y: v.bola.deY }, destino, k);
      v.img.setPosition(p.x, p.y - ALTURA_DO_CORPO * (alvo ? 1 : 1 - k) - p.altura).setDepth(p.y + 40);
      v.sombra
        .setPosition(p.x, p.y)
        .setDepth(p.y - 1)
        .setScale(1 - p.altura / 120);
      if (k < 1) continue;
      v.img.destroy();
      v.sombra.destroy();
      this.voos.splice(i, 1);
      this.rebentar(agora, destino, alvo ? v.bola.targetId : null);
    }

    // A neve agarrada a quem levou com a bola, e o "splash!" por cima.
    for (let i = this.marcas.length - 1; i >= 0; i -= 1) {
      const m = this.marcas[i];
      if (!m) continue;
      const onde = q.posicao(m.userId);
      const falta = m.ate - agora;
      if (!onde || falta <= 0) {
        m.img.destroy();
        m.texto.destroy();
        this.marcas.splice(i, 1);
        continue;
      }
      const alfa = Math.min(1, falta / 400);
      m.img
        .setPosition(onde.x + 2, onde.y - ALTURA_DO_CORPO)
        .setDepth(onde.y + 2)
        .setAlpha(alfa);
      const subiu = (1 - falta / 1600) * 18;
      m.texto
        .setPosition(onde.x, onde.y - 52 - subiu)
        .setDepth(1_000_002)
        .setAlpha(alfa);
    }

    for (let i = this.chao.length - 1; i >= 0; i -= 1) {
      const c = this.chao[i];
      if (!c) continue;
      const falta = c.ate - agora;
      if (falta <= 0) {
        c.img.destroy();
        this.chao.splice(i, 1);
      } else {
        c.img.setAlpha(Math.min(1, falta / 600));
      }
    }
  }

  /** A neve acabou de vez: a bola na mão derrete. */
  semNeve(): void {
    if (this.estadoAtual === "na_mao" || this.estadoAtual === "a_apanhar") this.mudar("sem_neve");
  }

  private rebentar(agora: number, onde: { x: number; y: number }, userId: string | null): void {
    const y = userId ? onde.y - ALTURA_DO_CORPO : onde.y;
    this.pedacos.explode(userId ? 14 : 10, onde.x, y);
    if (userId) {
      this.marcas.push({
        userId,
        ate: agora + 1600,
        img: this.scene.add.image(onde.x, y, T_MARCA),
        texto: this.scene.add
          .text(onde.x, onde.y - 52, "splash!", {
            fontFamily: "Inter, system-ui, sans-serif",
            fontSize: "11px",
            fontStyle: "bold",
            color: "#e8f4ff",
            stroke: "#1b3a5c",
            strokeThickness: 3,
          })
          .setOrigin(0.5, 1)
          .setResolution(3),
      });
    } else {
      this.chao.push({
        ate: agora + 1500,
        img: this.scene.add
          .image(onde.x, onde.y, T_MARCA)
          .setScale(1.1, 0.6)
          .setDepth(onde.y - 2),
      });
    }
  }

  private mudar(estado: EstadoDaBola): void {
    if (estado === this.estadoAtual) return;
    this.estadoAtual = estado;
    this.aoMudar(estado);
  }
}

/** A distância a que F atira quando não há ninguém à frente. */
export const DISTANCIA_SEM_ALVO = 5 * TILE;
